import React, { useEffect, useState } from "react";
import api from "api";
import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentForm from "components/stripe/StripePaymentForm";
import PaymentLinkForm from "components/orders/inc/PaymentLinkForm";

export default function RequestPaymentModal({
  show,
  requestId,
  onClose,
  onPaid
}) {
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentMode, setPaymentMode] = useState("immediate");
  const [sendingLink, setSendingLink] = useState(false);

  // -------------------------------
  // Charger paymentData
  // -------------------------------
  useEffect(() => {
    if (!show || !requestId) return;

    const loadPaymentData = async () => {
      setLoading(true);
      setPaymentData(null);
      api.handleError = false;
      try {
        const res = await api.get(
          `/formalities/request/${requestId}/paymentData`
        );
        const data = res?.data?.paymentData;
        if (!data) throw new Error("paymentData manquant");
        setPaymentData(data);
        setStripePromise(loadStripe(data.publicKey));
      } catch (e) {


        Swal.fire({
          title: "Erreur",
          text: e.response?.data?.error?.message ? e.response.data.error.message : "Impossible de charger les informations de paiement."
        });
        onClose();
      } finally {
        setLoading(false);
      }
    };

    loadPaymentData();
  }, [show, requestId]);

  // -------------------------------
  // Paiement Stripe
  // -------------------------------
  const handleStripeResult = async (status) => {
    if (status !== "success") {
      onClose();
      return;
    }

    Swal.fire({
      title: "Validation du paiement…",
      text: "Veuillez patienter quelques instants.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    await new Promise(resolve => setTimeout(resolve, 3000));

    try {
      const res = await api.get(
        `/formalities/request/${requestId}/checkPayment`
      );

      Swal.fire({
        title: "Paiement confirmé",
        text: "Votre demande de formalité va maintenant être traitée."
      });

      onPaid && onPaid(res.data);
      onClose();
    } catch (e) {
      Swal.fire({
        title: "Paiement confirmé",
        text: "Le paiement est validé, mais la mise à jour est retardée."
      });
      onClose();
    }
  };

  // -------------------------------
  // Génération lien de paiement
  // -------------------------------
  const handleSendPaymentLink = async () => {
    try {
      setSendingLink(true);

      await api.post(
        `/formalities/request/${requestId}/sendPaymentLink`
      );

      Swal.fire({
        title: "Lien envoyé",
        text: "Un lien de paiement a été envoyé au destinataire."
      });

      onClose();
    } catch (e) {
      Swal.fire({
        title: "Erreur",
        text: "Impossible d’envoyer le lien de paiement."
      });
    } finally {
      setSendingLink(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          {/* HEADER */}
          <div className="modal-header">
            <div className="modal-title">Paiement sécurisé</div>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            />
          </div>

          {/* BODY */}
          <div
            className="modal-body"
            style={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            {loading && (
              <div className="text-center py-4">
                Chargement des informations de paiement…
              </div>
            )}

            {!loading && paymentData && (
              <>
                {/* MODE DE REGLEMENT */}
                <div className="mb-4">
                  <label className="form-label small d-block mb-2">
                    Mode de règlement *
                  </label>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMode"
                      value="immediate"
                      checked={paymentMode === "immediate"}
                      onChange={() => setPaymentMode("immediate")}
                    />
                    <label className="form-check-label">
                      Paiement immédiat
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMode"
                      value="link"
                      checked={paymentMode === "link"}
                      onChange={() => setPaymentMode("link")}
                    />
                    <label className="form-check-label">
                      Envoyer un lien de paiement à un tiers
                    </label>
                  </div>
                </div>

                <p className="text-muted mb-3">
                  Montant à régler :{" "}
                  <strong>{paymentData.orderBalance} €</strong>
                </p>

                {/* STRIPE */}
                {paymentMode === "immediate" &&
                  stripePromise && (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret:
                          paymentData.clientSecret
                      }}
                    >
                      <StripePaymentForm
                        onResult={handleStripeResult}
                      />
                    </Elements>
                  )}

                {/* LIEN */}
                {paymentMode === "link" && paymentData && (
                  <PaymentLinkForm
                    orderId={paymentData.orderId}
                    orderBalance={paymentData.orderBalance}
                    defaultRecipient={{
                      recipientFirstname: paymentData.orderBillingFirstname,
                      recipientLastname: paymentData.orderBillingLastname,
                      recipientEmail: paymentData.orderBillingEmail,
                    }}
                    onSuccess={() => {
                      onClose();
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}